// Весь доступ к серверу для /ai — в одном файле.
// Токены лежат в localStorage (а не в sessionStorage, как у основного MES):
// руководитель не должен вводить пароль каждый день, пока жив refresh.
// Ключи свои (ai_*), чтобы не пересекаться с основным интерфейсом.
import { API_BASE_URL } from '../api';
import { saveSessionUser } from '../utils/session';
import { mockBrief, mockChat, mockConversations, mockConversation } from './mocks';

export const NO_ACCESS = 'Нет доступа к ИИ-помощнику';
// Должен совпадать с AI_ROLES в backend/apps/ai/scope.py (проверено 20.10: одинаковы).
const AI_ROLES = ['admin', 'director', 'investor'];
const A = 'ai_access';
const R = 'ai_refresh';

export class ApiError extends Error {
  constructor(status, message, code) { super(message); this.status = status; this.code = code || ''; }
}

// Режим моков: ?mock=1 запоминаем на сессию, чтобы переходы между экранами его не теряли.
function mockOn() {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get('mock') === '1') sessionStorage.setItem('ai_mock', '1');
    return sessionStorage.getItem('ai_mock') === '1';
  } catch (e) { return false; }
}
export const isMock = mockOn;
const isDev = process.env.NODE_ENV !== 'production';

// Вход в MES (/login) хранит токены в sessionStorage, вход в /ai — в localStorage.
// Кто уже вошёл в MES, работает в /ai на тех же токенах из sessionStorage и в
// localStorage ничего не копирует: иначе вход директора или админа пережил бы
// закрытие вкладки на общем компьютере и отдал бы MES с правом записи
// следующему человеку (review 20.10). Свой вход /ai — в localStorage.
function inMes() {
  try { return !!sessionStorage.getItem('refresh_token'); } catch (e) { return false; }
}
function getTok(kind) {
  if (inMes()) return sessionStorage.getItem(kind === 'a' ? 'access_token' : 'refresh_token');
  return localStorage.getItem(kind === 'a' ? A : R);
}
// Обновлённые токены пишем туда, где они живут; свой вход /ai (если он есть)
// обновляем тоже — после ротации refresh старый в localStorage уже не годится.
function setTok(kind, v) {
  if (inMes()) sessionStorage.setItem(kind === 'a' ? 'access_token' : 'refresh_token', v);
  if (localStorage.getItem(R)) localStorage.setItem(kind === 'a' ? A : R, v);
}

// Инвестору (в MES только чтение) вход запоминаем, как при входе через /ai:
// с телефона он открывает приложение каждый день, а iPhone чистит
// sessionStorage, когда PWA выгружается.
function keepInvestor() {
  try {
    if (!inMes() || sessionStorage.getItem('role') !== 'investor') return;
    localStorage.setItem(A, sessionStorage.getItem('access_token') || '');
    localStorage.setItem(R, sessionStorage.getItem('refresh_token'));
  } catch (e) { /* */ }
}

export const hasSession = () => { keepInvestor(); return mockOn() || !!getTok('r'); };

// Открыть отчёт MES в этом же окне (в iPhone-PWA _blank уходит в Safari и вход
// теряется). Переносим вход /ai в сессию MES — только инвестору: у него в MES
// одно чтение. Директор и админ идут в отчёт со своим входом MES, иначе экран
// попросит пароль. Сбой (нет связи) не мешает перейти.
export async function bridgeToMes() {
  try {
    if (inMes() || mockOn()) return;
    const me = await request('GET', '/production/me/');
    if (!me || me.role !== 'investor') return;
    const a = localStorage.getItem(A);
    const r = localStorage.getItem(R);
    if (!a || !r) return;
    sessionStorage.setItem('access_token', a);
    sessionStorage.setItem('refresh_token', r);
    saveSessionUser(me);
  } catch (e) { /* */ }
}

export function logout() {
  localStorage.removeItem(A);
  localStorage.removeItem(R);
  // Выход из /ai — это и выход из MES в этом окне, иначе /ai тут же
  // подхватил бы вход MES из sessionStorage.
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem('role');
  sessionStorage.removeItem('ai_chat');
  sessionStorage.removeItem('ai_mock');
}

async function refresh() {
  const r = getTok('r');
  if (!r) return false;
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: r }),
    });
    if (!res.ok) {
      // Только явный отказ сервера сбрасывает сессию; обрыв сети — нет.
      if (res.status === 401 || res.status === 400) logout();
      return false;
    }
    const d = await res.json();
    if (d.access) setTok('a', d.access);
    if (d.refresh) setTok('r', d.refresh);
    return !!d.access;
  } catch (e) { return false; }
}

async function request(method, path, body, retried) {
  const headers = { 'Content-Type': 'application/json', 'X-Lang': 'ru' };
  const t = getTok('a');
  if (t) headers.Authorization = `Bearer ${t}`;
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method, headers, body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError(0, 'Нет связи с сервером. Проверьте интернет.');
  }
  if (res.status === 401 && !retried && path !== '/auth/login/') {
    if (await refresh()) return request(method, path, body, true);
    throw new ApiError(401, 'Войдите заново');
  }
  let data = null;
  try { data = await res.json(); } catch (e) { /* пустой ответ */ }
  if (!res.ok) {
    // 403/429/503/504 — текст из ответа бэкенда ({detail}) не показываем:
    // у каждого кода есть своя понятная фраза в errorText.
    const own = [403, 429, 503, 504].includes(res.status);
    throw new ApiError(res.status, own ? '' : (data && (data.detail || data.error)) || '', data && data.code);
  }
  return data;
}

export async function login(username, password) {
  if (mockOn()) return true;
  let d;
  try {
    d = await request('POST', '/auth/login/', { username, password });
  } catch (e) {
    if (e.status === 401 || e.status === 400) throw new ApiError(401, 'Неверный логин или пароль');
    throw e;
  }
  localStorage.setItem(A, d.access);
  localStorage.setItem(R, d.refresh);
  // Ответ логина — только {access, refresh}; роль берём из /production/me/.
  // Если роль не руководящая — сразу выходим: ИИ ей не доступен.
  try {
    const me = await request('GET', '/production/me/');
    if (me && me.role && !AI_ROLES.includes(me.role) && !me.is_superuser) {
      logout();
      throw new ApiError(403, NO_ACCESS);
    }
  } catch (e) {
    if (e.status === 403) throw e;
    /* /me недоступен — решит сам /ai/ (403) */
  }
  return true;
}

// 404 в dev — бэкенд ещё не написан: падаем в мок.
async function withMock(real, mock) {
  if (mockOn()) return mock();
  try { return await real(); } catch (e) {
    if (isDev && e.status === 404) return mock();
    throw e;
  }
}

// withSummary=false — без текста от ИИ (дешевле: инвестору он не нужен).
export const getBrief = (date, withSummary = true) =>
  withMock(() => request('GET', `/ai/brief/?date=${encodeURIComponent(date)}${withSummary ? '' : '&summary=0'}`),
    () => mockBrief(date));

// Кто вошёл: от роли зависит вид сводки.
export const getMe = () =>
  withMock(() => request('GET', '/production/me/'), () => ({ role: 'director' }));

export const sendChat = (message, conversationId) =>
  withMock(
    () => request('POST', '/ai/chat/', conversationId ? { message, conversation_id: conversationId } : { message }),
    () => mockChat(message, conversationId));

export const getConversations = () =>
  withMock(async () => {
    const d = await request('GET', '/ai/conversations/');
    return Array.isArray(d) ? d : (d && d.results) || [];
  }, mockConversations);

export const getConversation = (id) =>
  withMock(() => request('GET', `/ai/conversations/${id}/`), () => mockConversation(id));

// Политика конфиденциальности: текст и принятие. 403 consent_required приходит
// с любого экрана, пока текущая версия не принята.
export const CONSENT_REQUIRED = 'consent_required';
export const getConsent = () =>
  withMock(() => request('GET', '/ai/consent/'),
    () => ({ version: 1, accepted: true, text: '' }));
export const acceptConsent = (version) =>
  withMock(() => request('POST', '/ai/consent/', { version }),
    () => ({ version, accepted: true, text: '' }));

// Понятное сообщение для человека по коду ответа
export function errorText(e) {
  if (e.status === 403) return NO_ACCESS;
  if (e.status === 429) return 'Лимит вопросов на сегодня исчерпан';
  if (e.status === 503) return 'ИИ ещё не подключён';
  if (e.status === 401) return 'Войдите заново';
  if (e.status === 504) return 'ИИ не успел ответить. Попробуйте задать вопрос короче.';
  if (e.status === 404) return e.message || 'Не найдено';
  return e.message || 'Не удалось получить ответ. Попробуйте ещё раз.';
}
